import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../../services/Api/Organization/apiOrganization";
import {
	UpsellApi,
	UpsellConditionApi,
	UpsellConditionType,
} from "../../../../../../../../../../../services/Api/Organization/types";
import UpsellFormBasic from "./UpsellFormBasic";
import UpsellFormRules from "./UpsellFormRules";

interface Props {
	upsell: UpsellApi;
}

const getParsedConditions = (conditions: UpsellConditionApi[]) => {
	return conditions.map((condition) => {
		const isNumberInput =
			condition.type &&
			[
				UpsellConditionType.ORDER_TOTAL_PRICE,
				UpsellConditionType.ORDER_TOTAL_PRICE_NET,
				UpsellConditionType.ORDER_ITEM_COUNT,
				UpsellConditionType.GUEST_COUNT,
			].includes(condition.type);

		if (isNumberInput) {
			return condition;
		}

		if (typeof condition.value === "string") {
			condition.value = condition.value.split(",");
		}

		if (typeof condition.label === "string") {
			condition.label = condition.label.split(",");
		}

		return condition;
	});
};

const UpsellForm = ({ upsell }: Props) => {
	const form = useForm<UpsellApi>({
		criteriaMode: "all",
		defaultValues: {
			...upsell,
			conditions: getParsedConditions(upsell.conditions || []),
			entities: [...(upsell.entities || []).sort((a, b) => a.position - b.position)],
		},
	});
	const [loading, setLoading] = useState(false);
	const { handleSubmit, formState, reset, setError } = form;
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);
	const organization = useSelector(selectOrganization);
	const history = useHistory();
	const { t } = useTranslation();
	const { addFlash, addSuccessFlash } = useFlash();
	const [areConditionsVisible, setAreConditionsVisible] = useState((upsell.conditions || []).length > 0);
	const [isItemsTableVisible, setIsItemsTableVisible] = useState((upsell.entities || []).length > 0);

	const getParsedDataForm = (data: UpsellApi) => {
		data.conditions = (data.conditions || [])
			.filter(
				(condition) =>
					condition.type ||
					condition.value ||
					condition.value === 0 ||
					condition.operator ||
					condition.item_id ||
					condition.item_group_id ||
					condition.category_id
			)
			.map((condition) => {
				if (Array.isArray(condition.value)) {
					condition.value = condition.value.join(",");
				}

				return condition;
			});

		return data;
	};

	const onSubmit = handleSubmit(async (data: any) => {
		if (!validateCustomErrors()) {
			return;
		}

		try {
			setLoading(true);
			const newData = getParsedDataForm(data);
			if (data.id) {
				const params: Record<string, any> = {
					include: "expressions,expressions.conditions,entities,entities.entity",
				};
				const res = await apiOrganization.updateUpsell(newData, params);
				reset({
					...res,
					conditions: getParsedConditions(res.conditions || []),
					entities: [...(res.entities || []).sort((a, b) => a.position - b.position)],
				});

				!res.entities.length && setIsItemsTableVisible(false);
				!res.conditions.length && setAreConditionsVisible(false);

				addSuccessFlash(t("lib:common.flash.saved"));
			} else {
				const res = await apiOrganization.createUpsell(newData);
				history.push(`/${organization.id}/menu/upsells/${res.id}`);
				addSuccessFlash(t("lib:common.flash.saved"));
			}
		} catch (error) {
			handleError.form(error, setError, addFlash);
		} finally {
			setLoading(false);
		}
	});

	return (
		<FormDirty formState={formState} loading={loading} noValidate onSubmit={onSubmit}>
			<UpsellFormBasic form={form} setErrors={setErrors} upsell={upsell} />
			<UpsellFormRules
				form={form}
				setIsItemsTableVisible={setIsItemsTableVisible}
				isItemsTableVisible={isItemsTableVisible}
				setErrors={setErrors}
				setAreConditionsVisible={setAreConditionsVisible}
				areConditionsVisible={areConditionsVisible}
			/>
		</FormDirty>
	);
};

export default UpsellForm;
